import type { ReactNode } from 'react';

/**
 * Small presentational building blocks.
 *
 * Icons are inline SVG rather than an icon font or a package: the kiosk must
 * render correctly on the first paint while offline, and an icon that fails to
 * load would leave an unlabelled button in a public area.
 */

export type IconName =
  | 'home'
  | 'search'
  | 'calendar'
  | 'user'
  | 'users'
  | 'briefcase'
  | 'wrench'
  | 'truck'
  | 'check'
  | 'chevron'
  | 'back'
  | 'alert'
  | 'clock'
  | 'wifi-off'
  | 'shield'
  | 'file'
  | 'coins'
  | 'heater'
  | 'chat'
  | 'key'
  | 'help'
  | 'folder';

const PATHS: Record<IconName, ReactNode> = {
  home: <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 21c0-3.5 3.1-5.5 7-5.5s7 2 7 5.5M17 7.5a3.5 3.5 0 0 1 0 7M18 21c0-2.4-.8-4-2-5" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </>
  ),
  wrench: <path d="M21 4a6 6 0 0 1-8 7.5L5.5 19a2.1 2.1 0 0 1-3-3L10 8.5A6 6 0 0 1 17.5 3z" />,
  truck: (
    <>
      <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </>
  ),
  check: <path d="m5 13 4.5 4.5L19 7" />,
  chevron: <path d="m9 5 7 7-7 7" />,
  back: <path d="M15 5 8 12l7 7" />,
  alert: (
    <>
      <path d="M12 3 2 20h20z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M2 4 22 22" />
      <path d="M5 12.5a11 11 0 0 1 4-2.5M2 8.8A16 16 0 0 1 8 5.4M16 5.6a16 16 0 0 1 6 3.2M15 10.2a11 11 0 0 1 4 2.3M9 16a6 6 0 0 1 6 0" />
      <path d="M12 20h.01" />
    </>
  ),
  shield: <path d="M12 3 4 6v6c0 5 3.4 8.2 8 9 4.6-.8 8-4 8-9V6z" />,
  file: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </>
  ),
  coins: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
    </>
  ),
  heater: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7.5 5v14M12 5v14M16.5 5v14M5 19v2M19 19v2" />
    </>
  ),
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.6A8 8 0 1 1 21 12z" />,
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M10.8 12.2 20 3M16 7l3 3M14 9l2 2" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17h.01" />
    </>
  ),
  folder: <path d="M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />,
};

export function Icon({ name, size = 24 }: { name: IconName; size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name] ?? PATHS.user}
    </svg>
  );
}

export function iconFor(name: string): IconName {
  return (Object.keys(PATHS) as IconName[]).includes(name as IconName) ? (name as IconName) : 'user';
}

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  type = 'button',
  block,
  icon,
  ariaLabel,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  block?: boolean;
  icon?: IconName;
  ariaLabel?: string;
}): JSX.Element {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${block ? ' btn-block' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {icon ? <Icon name={icon} size={20} /> : null}
      {children}
    </button>
  );
}

export function Spinner(): JSX.Element {
  return <span className="spinner" role="status" aria-live="polite" />;
}

export interface Step {
  key: string;
  label: string;
}

/**
 * Progress indicator. Communicates state through position *and* a textual
 * "Step 2 of 4" for screen readers - never through colour alone.
 */
export function Steps({ steps, current }: { steps: Step[]; current: number }): JSX.Element {
  return (
    <nav className="steps" aria-label={`Schritt ${current + 1} von ${steps.length}`}>
      {steps.map((step, index) => (
        <div key={step.key} style={{ display: 'contents' }}>
          {index > 0 ? <span className="step-line" aria-hidden="true" /> : null}
          <span
            className={`step${index === current ? ' active' : ''}${index < current ? ' done' : ''}`}
            aria-current={index === current ? 'step' : undefined}
          >
            <span className="step-dot">{index < current ? <Icon name="check" size={13} /> : index + 1}</span>
            {step.label}
          </span>
        </div>
      ))}
    </nav>
  );
}

export function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'info' | 'warning' | 'danger';
  icon?: IconName;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`banner banner-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {icon ? <Icon name={icon} size={20} /> : null}
      <span>{children}</span>
    </div>
  );
}

export function CenterState({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'success' | 'warning' | 'danger';
  icon: IconName;
  title: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="center-state fade-in">
      <div className={`state-icon ${tone}`}>
        <Icon name={icon} size={46} />
      </div>
      <h1 className="headline" style={{ margin: 0 }}>
        {title}
      </h1>
      {children}
    </div>
  );
}
